import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import {
    Alert,
    Autocomplete,
    Button,
    Dialog,
    FilePicker,
    Heading,
    Pane,
    SelectField,
    Spinner,
    Strong,
    Switch,
    Table,
    Text,
    TextInput,
    toaster,
} from 'evergreen-ui';
import Request from '../../../util/Api';

class InventoryResyncModal extends Component {
    constructor(props) {
        super(props);

        this.state = {
            stockAccounts: [],
            cardGames: [],
            expansions: [],
            stockAccountId: '',
            cardGameId: '',
            cardGameMarketId: '',
            expansionId: '',
            file: null,
            dryRun: true,
            force: false,
            loadingExpansions: false,
            downloading: false,
            uploading: false,
            result: null,
        };
    }

    async componentDidMount() {
        try {
            const [stockAccountsRes, cardGamesRes] = await Promise.all([
                Request.get('/stock-accounts', {}, { _start: 0, _end: 1000 }),
                Request.get('/card-games', {}, {}),
            ]);
            this.setState({
                stockAccounts: stockAccountsRes.data || stockAccountsRes || [],
                cardGames: cardGamesRes.data || cardGamesRes || [],
            });
        } catch (err) {
            toaster.danger('Failed to load stock accounts or card games');
        }
    }

    loadExpansions = async (cardMarketId) => {
        if (!cardMarketId) {
            this.setState({ expansions: [], expansionId: '' });
            return;
        }
        this.setState({ loadingExpansions: true });
        try {
            const res = await Request.get('/card-games/card-expansions', {}, {
                _start: 0,
                _end: 2000,
                _sort: 'releaseDate',
                _order: 'DESC',
                extraFilters: JSON.stringify({ cardGameId: parseInt(cardMarketId) }),
            });
            this.setState({
                expansions: res.data || res || [],
                expansionId: '',
            });
        } catch (err) {
            toaster.danger('Failed to load expansions');
        } finally {
            this.setState({ loadingExpansions: false });
        }
    };

    handleClose = () => {
        this.setState({
            file: null,
            result: null,
            uploading: false,
            downloading: false,
        });
        this.props.onClose && this.props.onClose();
    };

    handleStockAccountChange = (e) => this.setState({ stockAccountId: e.target.value });

    handleCardGameSelect = (game) => {
        const id = game ? String(game.id) : '';
        const cardMarketId = game ? String(game.cardMarketId || '') : '';
        this.setState({ cardGameId: id, cardGameMarketId: cardMarketId });
        // CardGameExpansion.cardGameId / CardGameCard.cardGameId actually
        // reference CardGame.cardMarketId (see model associations), so the
        // expansion list (and the downstream gameId filter) must use
        // cardMarketId, not the local PK.
        this.loadExpansions(cardMarketId);
    };

    handleExpansionSelect = (expansion) => {
        this.setState({ expansionId: expansion ? String(expansion.id) : '' });
    };

    handleFileChange = (files) => {
        if (files && files.length > 0) {
            this.setState({ file: files[0], result: null });
        }
    };

    handleDownload = async () => {
        const { expansionId, stockAccountId, cardGameMarketId } = this.state;
        if (!expansionId || !stockAccountId) {
            toaster.warning('Pick a stock account, game and set first');
            return;
        }

        this.setState({ downloading: true });
        try {
            const params = { expansionId, stockAccountId };
            if (cardGameMarketId) params.gameId = cardGameMarketId;

            const response = await Request.get(
                '/card-games/inventory/csv-template',
                {},
                params,
                { responseType: 'blob' }
            );

            const blob = response instanceof Blob
                ? response
                : new Blob([typeof response === 'string' ? response : JSON.stringify(response)], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `inventory_sa${stockAccountId}_exp${expansionId}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toaster.success('CSV downloaded');
        } catch (err) {
            toaster.danger('Download failed: ' + (err.message || 'unknown error'));
        } finally {
            this.setState({ downloading: false });
        }
    };

    handleUpload = async () => {
        const { file, expansionId, stockAccountId, dryRun, force } = this.state;
        if (!file) {
            toaster.warning('Select a CSV file first');
            return;
        }
        if (!stockAccountId) {
            toaster.warning('Pick a stock account first');
            return;
        }

        this.setState({ uploading: true, result: null });
        try {
            const formData = new FormData();
            formData.append('file', file);
            // Expansion is optional — when omitted, the API matches CSV ids
            // globally instead of scoping them to a single set.
            if (expansionId) formData.append('expansionId', expansionId);
            formData.append('stockAccountId', stockAccountId);
            formData.append('dryRun', dryRun ? '1' : '0');
            if (force) formData.append('force', '1');

            const response = await Request.post(
                '/card-games/inventory/import',
                null,
                {},
                formData
            );

            this.setState({ result: response });

            if (response && response.duplicate) {
                toaster.warning('Existing open adjustment found');
            } else if (dryRun) {
                toaster.success(`Validated — ${response.summary.changes} change(s) ready`);
            } else if (response.adjustmentId) {
                toaster.success(`Inventory adjustment #${response.adjustmentReference} created`);
            } else {
                toaster.notify('Nothing to apply');
            }
        } catch (err) {
            toaster.danger('Upload failed: ' + (err.message || 'unknown error'));
        } finally {
            this.setState({ uploading: false });
        }
    };

    renderResult() {
        const { result } = this.state;
        if (!result) return null;
        const { summary, preview } = result;

        return (
            <Pane marginTop={16} background="tint1" padding={12} borderRadius={6}>
                <Heading size={400} marginBottom={8}>
                    {result.duplicate
                        ? 'Duplicate detected'
                        : (result.dryRun ? 'Validation result' : 'Adjustment created')}
                </Heading>

                {result.duplicate && result.existing && (
                    <Alert intent="warning" marginBottom={8} title="Open adjustment already exists">
                        <Pane marginBottom={4}>{result.message}</Pane>
                        <Link to={`/stock-operations/${result.existing.id}`}>
                            <Strong color="selected">
                                Open adjustment #{result.existing.reference || result.existing.id}
                            </Strong>
                        </Link>
                        {' '}(status: {result.existing.status})
                        <Pane marginTop={6} fontSize={11}>
                            Tip: process or delete it first, or enable <Strong>Force</Strong> below to create a stacked adjustment anyway.
                        </Pane>
                    </Alert>
                )}

                {!result.duplicate && !result.dryRun && result.adjustmentId && (
                    <Alert intent="success" marginBottom={8}>
                        <Link to={`/stock-operations/${result.adjustmentId}`}>
                            <Strong color="selected">
                                Open adjustment #{result.adjustmentReference || result.adjustmentId}
                            </Strong>
                        </Link>
                        {' '}({result.adjustmentStatus}) created.
                    </Alert>
                )}

                <Pane display="flex" gap={16} marginBottom={8} flexWrap="wrap" fontSize={12}>
                    <Text size={300}><Strong>Rows:</Strong> {summary.totalRows}</Text>
                    <Text size={300}><Strong>Targets:</Strong> {summary.aggregatedTargets}</Text>
                    <Text size={300}><Strong>Matched:</Strong> {summary.matched}</Text>
                    <Text size={300}><Strong>Unchanged:</Strong> {summary.unchanged}</Text>
                    <Text size={300}><Strong>Changes:</Strong> {summary.changes}</Text>
                    <Text size={300}><Strong>Unmatched:</Strong> {summary.unmatched.length}</Text>
                    <Text size={300}><Strong>Mismatches:</Strong> {(summary.mismatches || []).length}</Text>
                    <Text size={300}><Strong>Invalid:</Strong> {summary.invalidRows.length}</Text>
                </Pane>

                {summary.unmatched.length > 0 && (
                    <Pane marginBottom={8}>
                        <Heading size={200}>Unmatched</Heading>
                        <Pane fontSize={11} maxHeight={100} overflowY="auto">
                            {summary.unmatched.slice(0, 50).map((u, i) => (
                                <div key={i}>id={u.id} {u.code ? `(${u.code} [${u.version}]) ` : ''}qty={u.quantity} — {u.reason}</div>
                            ))}
                        </Pane>
                    </Pane>
                )}

                {(summary.mismatches || []).length > 0 && (
                    <Pane marginBottom={8}>
                        <Heading size={200}>Code/version mismatches (id still used)</Heading>
                        <Pane fontSize={11} maxHeight={100} overflowY="auto">
                            {summary.mismatches.slice(0, 50).map((u, i) => (
                                <div key={i}>id={u.id}: {u.field} in CSV="{u.csv}" actual="{u.actual}"</div>
                            ))}
                        </Pane>
                    </Pane>
                )}

                {summary.invalidRows.length > 0 && (
                    <Pane marginBottom={8}>
                        <Heading size={200}>Invalid rows</Heading>
                        <Pane fontSize={11} maxHeight={100} overflowY="auto">
                            {summary.invalidRows.slice(0, 50).map((u, i) => (
                                <div key={i}>line {u.line}: {u.reason}</div>
                            ))}
                        </Pane>
                    </Pane>
                )}

                {preview && preview.length > 0 && (
                    <Pane>
                        <Heading size={200} marginBottom={4}>
                            Changes preview ({preview.length}{preview.length === 200 ? '+' : ''})
                        </Heading>
                        <Table>
                            <Table.Head>
                                <Table.TextHeaderCell>ID</Table.TextHeaderCell>
                                <Table.TextHeaderCell>Code</Table.TextHeaderCell>
                                <Table.TextHeaderCell>Rarity</Table.TextHeaderCell>
                                <Table.TextHeaderCell>Version</Table.TextHeaderCell>
                                <Table.TextHeaderCell>Name</Table.TextHeaderCell>
                                <Table.TextHeaderCell>Cur.</Table.TextHeaderCell>
                                <Table.TextHeaderCell>Target</Table.TextHeaderCell>
                                <Table.TextHeaderCell>Delta</Table.TextHeaderCell>
                            </Table.Head>
                            <Table.Body height={200}>
                                {preview.map((p) => (
                                    <Table.Row key={p.cardId}>
                                        <Table.TextCell isNumber>{p.id}</Table.TextCell>
                                        <Table.TextCell>{p.code}</Table.TextCell>
                                        <Table.TextCell>{p.rarity}</Table.TextCell>
                                        <Table.TextCell>{p.version}</Table.TextCell>
                                        <Table.TextCell>{p.name}</Table.TextCell>
                                        <Table.TextCell isNumber>{p.current}</Table.TextCell>
                                        <Table.TextCell isNumber>{p.target}</Table.TextCell>
                                        <Table.TextCell isNumber>
                                            <Strong color={p.delta > 0 ? 'green' : 'red'}>
                                                {p.delta > 0 ? '+' : ''}{p.delta}
                                            </Strong>
                                        </Table.TextCell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </Pane>
                )}
            </Pane>
        );
    }

    render() {
        const {
            stockAccounts,
            cardGames,
            expansions,
            stockAccountId,
            cardGameId,
            expansionId,
            dryRun,
            force,
            loadingExpansions,
            downloading,
            uploading,
            file,
        } = this.state;

        const selectedGame = cardGames.find(g => String(g.id) === cardGameId) || null;
        const selectedExpansion = expansions.find(e => String(e.id) === expansionId) || null;

        return (
            <Dialog
                isShown={this.props.isShown}
                title="Inventory resync (TCG singles)"
                onCloseComplete={this.handleClose}
                hasFooter={false}
                width={720}
                shouldCloseOnOverlayClick={false}
            >
                <Pane>
                    <Pane color="muted" marginBottom={12} fontSize={12}>
                        Download a CSV (<Strong>id, code, rarity, quantity, version</Strong>)
                        prepopulated with the current on-hand stock for the chosen warehouse + set,
                        edit the <Strong>quantity</Strong> column offline, then upload it back.
                        <Strong> id</Strong> is the sync discriminator; <Strong>code</Strong>,
                        <Strong> rarity</Strong> and <Strong>version</Strong> are kept for
                        readability (mismatches are surfaced as warnings but do not block the
                        sync). Quantities are absolute on-hand targets; deltas are computed
                        server-side and applied as a single inventory adjustment.
                    </Pane>

                    <Pane display="flex" gap={12} flexWrap="wrap" marginBottom={12}>
                        <SelectField
                            label="Warehouse"
                            value={stockAccountId}
                            onChange={this.handleStockAccountChange}
                            width={220}
                            marginBottom={0}
                        >
                            <option value="">— pick one —</option>
                            {stockAccounts.map((sa) => (
                                <option key={sa.id} value={sa.id}>{sa.name} (#{sa.id})</option>
                            ))}
                        </SelectField>

                        <Pane>
                            <Strong size={300} display="block" marginBottom={4}>Game</Strong>
                            <Autocomplete
                                items={cardGames}
                                itemToString={(g) => (g ? g.name : '')}
                                selectedItem={selectedGame}
                                onChange={this.handleCardGameSelect}
                            >
                                {({ getInputProps, getRef, inputValue, openMenu }) => (
                                    <TextInput
                                        ref={getRef}
                                        width={220}
                                        placeholder="Search game..."
                                        value={inputValue}
                                        onFocus={openMenu}
                                        {...getInputProps()}
                                    />
                                )}
                            </Autocomplete>
                        </Pane>

                        <Pane>
                            <Strong size={300} display="block" marginBottom={4}>
                                Set {loadingExpansions ? '(loading...)' : ''}
                            </Strong>
                            <Autocomplete
                                items={expansions}
                                itemToString={(e) => {
                                    if (!e) return '';
                                    const code = e.code ? `[${e.code}] ` : '';
                                    const date = e.releaseDate
                                        ? ` (${String(e.releaseDate).slice(0, 10)})`
                                        : '';
                                    return `${code}${e.name}${date}`;
                                }}
                                selectedItem={selectedExpansion}
                                onChange={this.handleExpansionSelect}
                            >
                                {({ getInputProps, getRef, inputValue, openMenu }) => (
                                    <TextInput
                                        ref={getRef}
                                        width={260}
                                        placeholder="Search set..."
                                        value={inputValue}
                                        onFocus={openMenu}
                                        disabled={!cardGameId || loadingExpansions}
                                        {...getInputProps()}
                                    />
                                )}
                            </Autocomplete>
                        </Pane>
                    </Pane>

                    <Pane display="flex" gap={12} alignItems="center" marginBottom={16} flexWrap="wrap">
                        <Button
                            appearance="primary"
                            onClick={this.handleDownload}
                            disabled={!expansionId || !stockAccountId || downloading}
                            iconBefore={downloading ? <Spinner size={14} /> : 'download'}
                        >
                            {downloading ? 'Generating...' : 'Download CSV'}
                        </Button>

                        <Pane width={1} height={28} background="border" />

                        <FilePicker
                            onChange={this.handleFileChange}
                            placeholder={file ? file.name : 'Pick CSV to upload'}
                            accept=".csv,text/csv"
                            width={260}
                        />

                        <Pane display="flex" alignItems="center" gap={6}>
                            <Switch
                                checked={dryRun}
                                onChange={(e) => this.setState({ dryRun: e.target.checked })}
                            />
                            <Text size={300}>Dry run</Text>
                        </Pane>

                        <Pane display="flex" alignItems="center" gap={6}>
                            <Switch
                                checked={force}
                                onChange={(e) => this.setState({ force: e.target.checked })}
                            />
                            <Text size={300} color={force ? 'danger' : undefined}>
                                Force (allow stacked adjustments)
                            </Text>
                        </Pane>

                        <Button
                            appearance="primary"
                            intent={dryRun ? 'none' : 'success'}
                            onClick={this.handleUpload}
                            disabled={uploading || !file || !stockAccountId}
                            iconBefore={uploading ? <Spinner size={14} /> : 'upload'}
                        >
                            {uploading
                                ? 'Processing...'
                                : (dryRun ? 'Validate' : 'Apply')}
                        </Button>
                    </Pane>

                    {this.renderResult()}
                </Pane>
            </Dialog>
        );
    }
}

export default InventoryResyncModal;
